import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import axios from 'axios';
import { 
  Zap, LogOut, Calendar as CalendarIcon, Clock, 
  User, Phone, Mail, Bike, FileText, Upload,
  CheckCircle2, XCircle, Loader2, Eye, AlertCircle,
  Store, Wrench, Settings, Save, Users, Send, UserCheck,
  Search, Filter, X, Trash2, ImageIcon, ZoomIn, Plus, ChevronLeft, ChevronRight
} from 'lucide-react';
import LoadingOverlay from '@/components/LoadingOverlay';
import OutlookCalendarView from '@/components/OutlookCalendarView';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShopDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isActionOpen, setIsActionOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bollaFile, setBollaFile] = useState(null);
  const [bollaPreview, setBollaPreview] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  
  // Filter state for bookings
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterModel, setFilterModel] = useState('');
  
  // Filter state for customers tab
  const [customerSearchText, setCustomerSearchText] = useState('');
  
  // Email dialog state
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [emailForm, setEmailForm] = useState({ subject: '', message: '' });
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState({
    mail_negozio: '',
    numero_veicoli_giorno: 5,
    smtp_user: '',
    smtp_pass: ''
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  
  // Delete customer state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [isDeletingCustomer, setIsDeletingCustomer] = useState(false);
  
  // Customer history dialog state
  const [isCustomerHistoryOpen, setIsCustomerHistoryOpen] = useState(false);
  const [selectedCustomerForHistory, setSelectedCustomerForHistory] = useState(null);
  const [customerBookings, setCustomerBookings] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  
  // Image viewer state
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [viewerImage, setViewerImage] = useState(null);
  const [viewerTitle, setViewerTitle] = useState('');
  
  // Libretto upload state (for shop to upload customer's libretto)
  const [shopLibrettoFile, setShopLibrettoFile] = useState(null);
  const [shopLibrettoPreview, setShopLibrettoPreview] = useState(null);
  const [isUploadingShopLibretto, setIsUploadingShopLibretto] = useState(false);
  
  // Quick libretto upload dialog (from customer list)
  const [isQuickLibrettoDialogOpen, setIsQuickLibrettoDialogOpen] = useState(false);
  const [quickLibrettoCustomer, setQuickLibrettoCustomer] = useState(null);
  const [quickLibrettoFile, setQuickLibrettoFile] = useState(null);
  const [quickLibrettoPreview, setQuickLibrettoPreview] = useState(null);
  const [isUploadingQuickLibretto, setIsUploadingQuickLibretto] = useState(false);
  
  const [actionForm, setActionForm] = useState({
    motivo_annullamento: '',
    lavori_effettuati: '',
    note_negozio: ''
  });

  // Calendar refresh trigger - incremented to force calendar refresh
  const [calendarRefreshTrigger, setCalendarRefreshTrigger] = useState(0);

  // Create booking dialog state
  const [isCreateBookingOpen, setIsCreateBookingOpen] = useState(false);
  const [createBookingStep, setCreateBookingStep] = useState('select'); // 'select', 'select_vehicle', 'new_customer', 'booking_details'
  const [selectedCustomerForBooking, setSelectedCustomerForBooking] = useState(null);
  const [customerSearchForBooking, setCustomerSearchForBooking] = useState('');
  const [customerVehicles, setCustomerVehicles] = useState([]);
  const [selectedVehicleForBooking, setSelectedVehicleForBooking] = useState(null);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
  const [newCustomerForm, setNewCustomerForm] = useState({
    nome: '',
    cognome: '',
    email: '',
    telefono: '',
    modello_moto: '',
    password: '',
    libretto_base64: null
  });
  const [bookingForm, setBookingForm] = useState({
    data_consegna: '',
    ora_consegna: '',
    ora_ritiro: '',
    km_attuali: '',
    note: ''
  });
  const [isCreatingBooking, setIsCreatingBooking] = useState(false);
  const [newCustomerLibrettoFile, setNewCustomerLibrettoFile] = useState(null);
  const [newCustomerLibrettoPreview, setNewCustomerLibrettoPreview] = useState(null);
  
  // Model series state for new customer
  const [modelSeries, setModelSeries] = useState([]);
  const [selectedSeriesForNewCustomer, setSelectedSeriesForNewCustomer] = useState('');
  const [filteredModelsForNewCustomer, setFilteredModelsForNewCustomer] = useState([]);

  useEffect(() => {
    fetchBookings();
    fetchSettings();
    fetchCustomers();
    fetchModelSeries();
  }, [token]);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API_URL}/bookings/shop`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data.bookings);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Errore nel caricamento delle prenotazioni');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/shop/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data.settings);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_URL}/shop/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data.customers);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchModelSeries = async () => {
    try {
      const response = await axios.get(`${API_URL}/model-series`);
      setModelSeries(response.data.series);
    } catch (error) {
      console.error('Error fetching model series:', error);
    }
  };

  // Filter models when series changes for new customer
  useEffect(() => {
    if (selectedSeriesForNewCustomer && selectedSeriesForNewCustomer !== 'all') {
      const series = modelSeries.find(s => s.id === selectedSeriesForNewCustomer);
      if (series) {
        setFilteredModelsForNewCustomer(series.models);
      }
    } else {
      // Show all models
      const allModels = [];
      modelSeries.forEach(series => {
        series.models.forEach(model => {
          allModels.push({
            ...model,
            seriesName: series.name
          });
        });
      });
      setFilteredModelsForNewCustomer(allModels);
    }
    // Reset model selection when series changes
    setNewCustomerForm(prev => ({ ...prev, modello_moto: '' }));
  }, [selectedSeriesForNewCustomer, modelSeries]);
  
  // Get unique models from bookings
  const uniqueModels = [...new Set(bookings.map(b => b.modello_moto))].sort();
  
  // Get unique customers from bookings
  const uniqueCustomers = [...new Set(bookings.map(b => `${b.customer_nome} ${b.customer_cognome}`))].sort();
  
  // Filter bookings based on filters
  const filterBookings = (bookingsList) => {
    return bookingsList.filter(booking => {
      const matchesCustomer = !filterCustomer || 
        `${booking.customer_nome} ${booking.customer_cognome}`.toLowerCase().includes(filterCustomer.toLowerCase());
      const matchesModel = !filterModel || filterModel === 'all' || booking.modello_moto === filterModel;
      return matchesCustomer && matchesModel;
    });
  };
  
  // Clear all filters
  const clearFilters = () => {
    setFilterCustomer('');
    setFilterModel('');
  };
  
  const hasActiveFilters = filterCustomer || (filterModel && filterModel !== 'all');
  
  // Sort customers alphabetically by cognome then nome
  const sortedCustomers = [...customers].sort((a, b) => {
    const cognomeCompare = a.cognome.localeCompare(b.cognome, 'it');
    if (cognomeCompare !== 0) return cognomeCompare;
    return a.nome.localeCompare(b.nome, 'it');
  });
  
  // Filter customers based on search text
  const filteredCustomers = sortedCustomers.filter(customer => {
    if (!customerSearchText) return true;
    const searchLower = customerSearchText.toLowerCase();
    return (
      customer.nome.toLowerCase().includes(searchLower) ||
      customer.cognome.toLowerCase().includes(searchLower) ||
      customer.email.toLowerCase().includes(searchLower) ||
      customer.telefono.toLowerCase().includes(searchLower) ||
      (customer.modello_moto && customer.modello_moto.toLowerCase().includes(searchLower))
    );
  });

  const handleSaveSettings = async () => {
    setIsSavingSettings(true);
    try {
      await axios.put(`${API_URL}/shop/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Impostazioni salvate con successo!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nel salvataggio';
      toast.error(message);
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleBollaChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBollaFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBollaPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAction = async () => {
    if (!selectedBooking || !actionType) return;
    setIsSubmitting(true);

    try {
      let bollaBase64 = null;
      if (bollaFile && actionType === 'complete') {
        const reader = new FileReader();
        bollaBase64 = await new Promise((resolve) => {
          reader.onloadend = () => {
            const base64 = reader.result.split(',')[1];
            resolve(base64);
          };
          reader.readAsDataURL(bollaFile);
        });
      }

      const statusMap = {
        confirm: 'confirmed',
        complete: 'completed',
        cancel: 'cancelled'
      };

      await axios.put(`${API_URL}/bookings/${selectedBooking.id}/status`, {
        status: statusMap[actionType],
        motivo_annullamento: actionForm.motivo_annullamento || null,
        lavori_effettuati: actionForm.lavori_effettuati || null,
        note_negozio: actionForm.note_negozio || null,
        foto_bolla_base64: bollaBase64
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const messages = {
        confirm: 'Tagliando confermato! Email inviata al cliente.',
        complete: 'Tagliando completato! Email inviata al cliente.',
        cancel: 'Tagliando annullato. Email inviata al cliente.'
      };

      toast.success(messages[actionType]);
      setIsActionOpen(false);
      setSelectedBooking(null);
      setActionType(null);
      setActionForm({ motivo_annullamento: '', lavori_effettuati: '', note_negozio: '' });
      setBollaFile(null);
      setBollaPreview(null);
      fetchBookings();
      // Trigger calendar refresh to update the event status
      setCalendarRefreshTrigger(prev => prev + 1);
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante l\'aggiornamento';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openActionDialog = (booking, type) => {
    setSelectedBooking(booking);
    setActionType(type);
    setIsActionOpen(true);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Email functions
  const toggleCustomerSelection = (customerId) => {
    setSelectedCustomers(prev => 
      prev.includes(customerId) 
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const selectAllCustomers = () => {
    if (selectedCustomers.length === filteredCustomers.length && filteredCustomers.length > 0) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(filteredCustomers.map(c => c.id));
    }
  };

  const openEmailDialog = (customerId = null) => {
    if (customerId) {
      setSelectedCustomers([customerId]);
    }
    setEmailForm({ subject: '', message: '' });
    setIsEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailForm.subject || !emailForm.message) {
      toast.error('Inserisci oggetto e messaggio');
      return;
    }

    setIsSendingEmail(true);
    try {
      const response = await axios.post(`${API_URL}/shop/send-email`, {
        customer_ids: selectedCustomers,
        subject: emailForm.subject,
        message: emailForm.message
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(response.data.message);
      setIsEmailDialogOpen(false);
      setSelectedCustomers([]);
      setEmailForm({ subject: '', message: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nell\'invio email';
      toast.error(message);
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Delete customer functions
  const openDeleteDialog = (customer) => {
    setCustomerToDelete(customer);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteCustomer = async () => {
    if (!customerToDelete) return;
    setIsDeletingCustomer(true);

    try {
      const response = await axios.delete(`${API_URL}/shop/customers/${customerToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Cliente ${customerToDelete.cognome} ${customerToDelete.nome} eliminato con ${response.data.bookings_deleted} prenotazioni`);
      setIsDeleteDialogOpen(false);
      setCustomerToDelete(null);
      // Refresh customers and bookings list
      fetchCustomers();
      fetchBookings();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nell\'eliminazione del cliente';
      toast.error(message);
    } finally {
      setIsDeletingCustomer(false);
    }
  };

  // Customer history functions
  const openCustomerHistory = async (customer) => {
    setSelectedCustomerForHistory(customer);
    setIsCustomerHistoryOpen(true);
    setIsLoadingHistory(true);
    
    try {
      // Fetch complete customer details with images
      const response = await axios.get(`${API_URL}/shop/customers/${customer.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fullCustomer = response.data.customer;
      const fullBookings = response.data.bookings;
      
      // Update with full data including images
      setSelectedCustomerForHistory(fullCustomer);
      
      // Sort by date descending (most recent first)
      fullBookings.sort((a, b) => new Date(b.data_consegna) - new Date(a.data_consegna));
      setCustomerBookings(fullBookings);
    } catch (error) {
      console.error('Error fetching customer details:', error);
      // Fallback to local data
      const customerHistory = bookings.filter(b => b.customer_id === customer.id);
      customerHistory.sort((a, b) => new Date(b.data_consegna) - new Date(a.data_consegna));
      setCustomerBookings(customerHistory);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Image viewer function
  const openImageViewer = (imageBase64, title) => {
    setViewerImage(imageBase64);
    setViewerTitle(title);
    setIsImageViewerOpen(true);
  };

  // View libretto from customer list
  const viewCustomerLibretto = async (customer) => {
    try {
      const response = await axios.get(`${API_URL}/shop/customers/${customer.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fullCustomer = response.data.customer;
      if (fullCustomer.libretto_image) {
        openImageViewer(fullCustomer.libretto_image, `Libretto - ${customer.cognome} ${customer.nome}`);
      } else {
        toast.error('Foto libretto non disponibile');
      }
    } catch (error) {
      toast.error('Errore nel caricamento del libretto');
    }
  };

  // Shop upload libretto for customer
  const handleShopLibrettoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setShopLibrettoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setShopLibrettoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadShopLibretto = async () => {
    if (!shopLibrettoFile || !selectedCustomerForHistory) return;
    
    setIsUploadingShopLibretto(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64 = reader.result.split(',')[1];
          
          const response = await axios.put(`${API_URL}/shop/customers/${selectedCustomerForHistory.id}/libretto`, {
            libretto_base64: base64
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          toast.success('Libretto caricato con successo! I dati sono stati estratti automaticamente.');
          setShopLibrettoFile(null);
          setShopLibrettoPreview(null);
          
          // Refresh customer data
          openCustomerHistory(selectedCustomerForHistory);
          fetchCustomers();
        } catch (error) {
          const message = error.response?.data?.detail || 'Errore nel caricamento del libretto';
          toast.error(message);
        } finally {
          setIsUploadingShopLibretto(false);
        }
      };
      reader.readAsDataURL(shopLibrettoFile);
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nel caricamento del libretto';
      toast.error(message);
      setIsUploadingShopLibretto(false);
    }
  };

  // Quick libretto upload from customer list
  const openQuickLibrettoDialog = (customer) => {
    setQuickLibrettoCustomer(customer);
    setQuickLibrettoFile(null);
    setQuickLibrettoPreview(null);
    setIsQuickLibrettoDialogOpen(true);
  };

  const handleQuickLibrettoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setQuickLibrettoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setQuickLibrettoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadQuickLibretto = async () => {
    if (!quickLibrettoFile || !quickLibrettoCustomer) return;
    
    setIsUploadingQuickLibretto(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64 = reader.result.split(',')[1];
          
          await axios.put(`${API_URL}/shop/customers/${quickLibrettoCustomer.id}/libretto`, {
            libretto_base64: base64
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          toast.success(`Libretto caricato per ${quickLibrettoCustomer.cognome} ${quickLibrettoCustomer.nome}!`);
          setIsQuickLibrettoDialogOpen(false);
          setQuickLibrettoFile(null);
          setQuickLibrettoPreview(null);
          setQuickLibrettoCustomer(null);
          fetchCustomers(); // Refresh list
        } catch (error) {
          const message = error.response?.data?.detail || 'Errore nel caricamento del libretto';
          toast.error(message);
        } finally {
          setIsUploadingQuickLibretto(false);
        }
      };
      reader.readAsDataURL(quickLibrettoFile);
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nel caricamento del libretto';
      toast.error(message);
      setIsUploadingQuickLibretto(false);
    }
  };

  // ============= CREATE BOOKING FROM SHOP =============
  
  const openCreateBookingDialog = () => {
    setIsCreateBookingOpen(true);
    setCreateBookingStep('select');
    setSelectedCustomerForBooking(null);
    setCustomerSearchForBooking('');
    setNewCustomerForm({
      nome: '',
      cognome: '',
      email: '',
      telefono: '',
      modello_moto: '',
      password: '',
      libretto_base64: null
    });
    setBookingForm({
      data_consegna: '',
      ora_consegna: '',
      ora_ritiro: '',
      km_attuali: '',
      note: ''
    });
    setNewCustomerLibrettoFile(null);
    setNewCustomerLibrettoPreview(null);
    setSelectedSeriesForNewCustomer('');
  };

  const handleNewCustomerLibrettoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewCustomerLibrettoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewCustomerLibrettoPreview(reader.result);
        // Store base64 in form
        const base64 = reader.result.split(',')[1];
        setNewCustomerForm(prev => ({ ...prev, libretto_base64: base64 }));
      };
      reader.readAsDataURL(file);
    }
  };

  const selectExistingCustomer = async (customer) => {
    setSelectedCustomerForBooking(customer);
    setSelectedVehicleForBooking(null);
    setIsLoadingVehicles(true);
    
    try {
      // Fetch customer's vehicles
      const response = await axios.get(`${API_URL}/shop/customers/${customer.id}/vehicles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const vehicles = response.data.vehicles || [];
      setCustomerVehicles(vehicles);
      
      if (vehicles.length === 0) {
        // No vehicles - use legacy modello_moto if available
        if (customer.modello_moto) {
          setSelectedVehicleForBooking({
            id: null,
            modello_moto: customer.modello_moto,
            nickname: null,
            is_legacy: true
          });
          setCreateBookingStep('booking_details');
        } else {
          toast.error('Questo cliente non ha veicoli registrati');
          setCreateBookingStep('select');
        }
      } else if (vehicles.length === 1) {
        // Only one vehicle - auto-select it
        setSelectedVehicleForBooking(vehicles[0]);
        setCreateBookingStep('booking_details');
      } else {
        // Multiple vehicles - show selection step
        setCreateBookingStep('select_vehicle');
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      // Fallback to legacy modello_moto
      if (customer.modello_moto) {
        setSelectedVehicleForBooking({
          id: null,
          modello_moto: customer.modello_moto,
          nickname: null,
          is_legacy: true
        });
        setCreateBookingStep('booking_details');
      } else {
        toast.error('Errore nel caricamento dei veicoli');
      }
    } finally {
      setIsLoadingVehicles(false);
    }
  };

  const goToNewCustomerForm = () => {
    setSelectedCustomerForBooking(null);
    setSelectedVehicleForBooking(null);
    setCustomerVehicles([]);
    setCreateBookingStep('new_customer');
  };

  const goToBookingDetails = () => {
    setCreateBookingStep('booking_details');
  };

  const calculateRitiroTime = (consegnaTime) => {
    if (!consegnaTime) return '';
    const [hours, minutes] = consegnaTime.split(':').map(Number);
    let ritiroHours = hours + 1;
    let ritiroMinutes = minutes + 30;
    
    if (ritiroMinutes >= 60) {
      ritiroMinutes -= 60;
      ritiroHours += 1;
    }
    
    // Skip lunch break
    if (ritiroHours === 13 && ritiroMinutes >= 30) {
      ritiroHours = 14;
      ritiroMinutes = ritiroMinutes;
    } else if (ritiroHours === 14 && ritiroMinutes < 30) {
      ritiroHours = 14;
      ritiroMinutes = 30;
    }
    
    return `${ritiroHours.toString().padStart(2, '0')}:${ritiroMinutes.toString().padStart(2, '0')}`;
  };

  const handleConsegnaChange = (time) => {
    setBookingForm(prev => ({
      ...prev,
      ora_consegna: time,
      ora_ritiro: calculateRitiroTime(time)
    }));
  };

  const createBookingFromShop = async () => {
    // Validate booking form
    if (!bookingForm.data_consegna || !bookingForm.ora_consegna || !bookingForm.ora_ritiro || !bookingForm.km_attuali) {
      toast.error('Compila tutti i campi obbligatori del tagliando');
      return;
    }

    // If new customer, validate customer form
    if (!selectedCustomerForBooking) {
      if (!newCustomerForm.nome || !newCustomerForm.cognome || !newCustomerForm.email || 
          !newCustomerForm.telefono || !newCustomerForm.modello_moto || !newCustomerForm.password) {
        toast.error('Compila tutti i campi obbligatori del cliente');
        return;
      }
    }

    // For existing customer, need selected vehicle
    if (selectedCustomerForBooking && !selectedVehicleForBooking) {
      toast.error('Seleziona un veicolo per la prenotazione');
      return;
    }

    setIsCreatingBooking(true);
    try {
      const payload = {
        data_consegna: bookingForm.data_consegna,
        ora_consegna: bookingForm.ora_consegna,
        ora_ritiro: bookingForm.ora_ritiro,
        km_attuali: parseInt(bookingForm.km_attuali),
        note: bookingForm.note || null
      };

      if (selectedCustomerForBooking) {
        // Existing customer
        payload.customer_id = selectedCustomerForBooking.id;
        payload.new_customer = false;
        // Add vehicle info - use modello_moto from selected vehicle
        payload.modello_moto = selectedVehicleForBooking.modello_moto;
        if (selectedVehicleForBooking.id && !selectedVehicleForBooking.is_legacy) {
          payload.vehicle_id = selectedVehicleForBooking.id;
        }
      } else {
        // New customer
        payload.new_customer = true;
        payload.nome = newCustomerForm.nome;
        payload.cognome = newCustomerForm.cognome;
        payload.email = newCustomerForm.email;
        payload.telefono = newCustomerForm.telefono;
        payload.modello_moto = newCustomerForm.modello_moto;
        payload.password = newCustomerForm.password;
        payload.libretto_base64 = newCustomerForm.libretto_base64;
      }

      const response = await axios.post(`${API_URL}/shop/bookings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(response.data.new_customer_created 
        ? 'Cliente creato e tagliando confermato! Email inviata al cliente.'
        : 'Tagliando creato e confermato! Email inviata al cliente.');
      
      setIsCreateBookingOpen(false);
      // Reset all states
      setSelectedCustomerForBooking(null);
      setSelectedVehicleForBooking(null);
      setCustomerVehicles([]);
      setCreateBookingStep('select');
      setBookingForm({
        data_consegna: '',
        ora_consegna: '',
        ora_ritiro: '',
        km_attuali: '',
        note: ''
      });
      setNewCustomerForm({
        nome: '',
        cognome: '',
        email: '',
        telefono: '',
        modello_moto: '',
        password: '',
        libretto_base64: null
      });
      fetchBookings();
      fetchCustomers();
      // Trigger calendar refresh to show the new event
      setCalendarRefreshTrigger(prev => prev + 1);
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nella creazione del tagliando';
      toast.error(message);
    } finally {
      setIsCreatingBooking(false);
    }
  };

  // Filter customers for booking selection
  const filteredCustomersForBooking = customers.filter(c => {
    const searchLower = customerSearchForBooking.toLowerCase();
    return (
      c.nome?.toLowerCase().includes(searchLower) ||
      c.cognome?.toLowerCase().includes(searchLower) ||
      c.email?.toLowerCase().includes(searchLower) ||
      c.telefono?.includes(customerSearchForBooking) ||
      c.modello_moto?.toLowerCase().includes(searchLower)
    );
  });

  // Generate available time slots for consegna
  const timeSlots = [];
  for (let hour = 10; hour <= 17; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
    if (hour < 17) {
      timeSlots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
  }

  // Generate available time slots for ritiro (from consegna time + 1h30 to 19:00)
  const getPickupSlots = (consegnaTime) => {
    if (!consegnaTime) return [];
    
    const [hours, minutes] = consegnaTime.split(':').map(Number);
    // Minimum pickup time is 1h30 after consegna
    let minPickupHours = hours + 1;
    let minPickupMinutes = minutes + 30;
    if (minPickupMinutes >= 60) {
      minPickupMinutes -= 60;
      minPickupHours += 1;
    }
    
    const slots = [];
    for (let hour = minPickupHours; hour <= 19; hour++) {
      if (hour === minPickupHours) {
        // For the first hour, start from minPickupMinutes
        if (minPickupMinutes <= 0) slots.push(`${hour.toString().padStart(2, '0')}:00`);
        if (minPickupMinutes <= 30) slots.push(`${hour.toString().padStart(2, '0')}:30`);
      } else {
        slots.push(`${hour.toString().padStart(2, '0')}:00`);
        if (hour < 19) {
          slots.push(`${hour.toString().padStart(2, '0')}:30`);
        }
      }
    }
    return slots;
  };

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_confirmation: { label: 'In Attesa', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
      confirmed: { label: 'Confermato', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
      completed: { label: 'Completato', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
      cancelled: { label: 'Annullato', className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' }
    };
    
    const config = statusConfig[status] || statusConfig.pending_confirmation;
    
    return (
      <Badge className={config.className}>
        {config.label}
      </Badge>
    );
  };

  const pendingBookings = filterBookings(bookings.filter(b => b.status === 'pending_confirmation'));
  const confirmedBookings = filterBookings(bookings.filter(b => b.status === 'confirmed'));
  const completedBookings = filterBookings(bookings.filter(b => b.status === 'completed'));
  const cancelledBookings = filterBookings(bookings.filter(b => b.status === 'cancelled'));
  
  // FilterBar JSX - inline to prevent focus loss on re-render
  const filterBarJSX = (
    <Card className="mb-4">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1 space-y-2">
            <Label className="text-xs text-muted-foreground flex items-center gap-1">
              <Search className="w-3 h-3" />
              Cerca Cliente
            </Label>
            <Input
              placeholder="Nome o cognome..."
              value={filterCustomer}
              onChange={(e) => setFilterCustomer(e.target.value)}
              className="h-10"
              data-testid="filter-customer-input"
            />
          </div>
          <div className="w-full sm:w-48 space-y-2">
            <Label className="text-xs text-muted-foreground flex items-center gap-1">
              <Bike className="w-3 h-3" />
              Modello Veicolo
            </Label>
            <Select value={filterModel} onValueChange={setFilterModel}>
              <SelectTrigger className="h-10" data-testid="filter-model-select">
                <SelectValue placeholder="Tutti i modelli" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i modelli</SelectItem>
                {uniqueModels.map(model => (
                  <SelectItem key={model} value={model}>{model}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {hasActiveFilters && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={clearFilters}
              className="h-10"
              data-testid="clear-filters-btn"
            >
              <X className="w-4 h-4 mr-1" />
              Pulisci
            </Button>
          )}
        </div>
        {hasActiveFilters && (
          <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
            <Filter className="w-4 h-4" />
            <span>Filtri attivi:</span>
            {filterCustomer && (
              <Badge variant="secondary">Cliente: {filterCustomer}</Badge>
            )}
            {filterModel && filterModel !== 'all' && (
              <Badge variant="secondary">Modello: {filterModel}</Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderBookingCard = (booking, showActions = true) => (
    <Card key={booking.id} className="border-border/50" data-testid={`shop-booking-card-${booking.id}`}>
      <CardContent className="p-4 md:p-6">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
          <div className="space-y-3 flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <CalendarIcon className="w-5 h-5 text-primary" />
              <span className="font-bold">
                {format(new Date(booking.data_consegna), 'EEEE d MMMM yyyy', { locale: it })}
              </span>
              {getStatusBadge(booking.status)}
            </div>

            <div className="grid sm:grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <span>{booking.customer_nome} {booking.customer_cognome}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>{booking.customer_email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span>{booking.customer_telefono}</span>
              </div>
              <div className="flex items-center gap-2">
                <Bike className="w-4 h-4 text-muted-foreground" />
                <span>{booking.modello_moto}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Consegna: {booking.ora_consegna}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Ritiro: {booking.ora_ritiro}
              </span>
              <span>Km: {booking.km_attuali}</span>
            </div>

            {booking.note && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium mb-1">Note cliente:</p>
                <p className="text-sm text-muted-foreground">{booking.note}</p>
              </div>
            )}

            {booking.status === 'completed' && booking.lavori_effettuati && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="text-sm font-medium text-green-700 dark:text-green-300 mb-1">Lavori effettuati:</p>
                <p className="text-sm text-green-600 dark:text-green-400 whitespace-pre-line">
                  {booking.lavori_effettuati}
                </p>
              </div>
            )}

            {booking.status === 'cancelled' && booking.motivo_annullamento && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-sm font-medium text-red-700 dark:text-red-300 mb-1">Motivo annullamento:</p>
                <p className="text-sm text-red-600 dark:text-red-400">
                  {booking.motivo_annullamento}
                </p>
              </div>
            )}
          </div>

          {showActions && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setSelectedBooking(booking); setIsDetailOpen(true); }}
                data-testid={`view-booking-${booking.id}`}
              >
                <Eye className="w-4 h-4 mr-1" />
                Dettagli
              </Button>
              
              {booking.status === 'pending_confirmation' && (
                <>
                  <Button
                    size="sm"
                    onClick={() => openActionDialog(booking, 'confirm')}
                    data-testid={`confirm-booking-${booking.id}`}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Conferma
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => openActionDialog(booking, 'cancel')}
                    data-testid={`cancel-booking-${booking.id}`}
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Rifiuta
                  </Button>
                </>
              )}
              
              {booking.status === 'confirmed' && (
                <>
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => openActionDialog(booking, 'complete')}
                    data-testid={`complete-booking-${booking.id}`}
                  >
                    <Wrench className="w-4 h-4 mr-1" />
                    Fatto
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => openActionDialog(booking, 'cancel')}
                    data-testid={`cancel-confirmed-${booking.id}`}
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Annulla
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="shop-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Zap className="w-8 h-8 text-primary" />
              <span className="font-heading font-black text-xl hidden sm:inline">NIU Service</span>
              <Badge variant="secondary" className="ml-2">Negozio</Badge>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-2 text-sm">
                <Store className="w-4 h-4 text-muted-foreground" />
                <span>{user?.nome_negozio}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="shop-logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/10 text-yellow-500 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{pendingBookings.length}</p>
                  <p className="text-xs text-muted-foreground">In Attesa</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{confirmedBookings.length}</p>
                  <p className="text-xs text-muted-foreground">Confermati</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 text-green-500 flex items-center justify-center">
                  <Wrench className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{completedBookings.length}</p>
                  <p className="text-xs text-muted-foreground">Completati</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 text-red-500 flex items-center justify-center">
                  <XCircle className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{cancelledBookings.length}</p>
                  <p className="text-xs text-muted-foreground">Annullati</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{customers.length}</p>
                  <p className="text-xs text-muted-foreground">Clienti</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Create Booking Button */}
        <div className="mb-6">
          <Button 
            onClick={openCreateBookingDialog}
            className="w-full sm:w-auto"
            data-testid="create-booking-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Tagliando
          </Button>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Responsive tabs: 2 cols on mobile, 3 on sm, 6 on md+ */}
          <div className="mb-6">
            <TabsList className="h-auto grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-1 w-full p-1 bg-muted/50">
              <TabsTrigger 
                value="pending" 
                data-testid="tab-pending"
                className="text-xs sm:text-sm px-2 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <span className="truncate">In Attesa ({pendingBookings.length})</span>
              </TabsTrigger>
              <TabsTrigger 
                value="confirmed" 
                data-testid="tab-confirmed"
                className="text-xs sm:text-sm px-2 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <span className="truncate">Confermati ({confirmedBookings.length})</span>
              </TabsTrigger>
              <TabsTrigger 
                value="completed" 
                data-testid="tab-completed"
                className="text-xs sm:text-sm px-2 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <span className="truncate">Completati ({completedBookings.length})</span>
              </TabsTrigger>
              <TabsTrigger 
                value="cancelled" 
                data-testid="tab-cancelled"
                className="text-xs sm:text-sm px-2 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <span className="truncate">Annullati ({cancelledBookings.length})</span>
              </TabsTrigger>
              <TabsTrigger 
                value="calendar" 
                data-testid="tab-calendar"
                className="text-xs sm:text-sm px-2 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <CalendarIcon className="w-4 h-4 mr-1 hidden sm:inline" />
                <span className="truncate">Calendario</span>
              </TabsTrigger>
              <TabsTrigger 
                value="customers" 
                data-testid="tab-customers"
                className="text-xs sm:text-sm px-2 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <Users className="w-4 h-4 mr-1 hidden sm:inline" />
                <span className="truncate">Clienti</span>
              </TabsTrigger>
              <TabsTrigger 
                value="settings" 
                data-testid="tab-settings"
                className="text-xs sm:text-sm px-2 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <Settings className="w-4 h-4 mr-1 hidden sm:inline" />
                <span className="truncate">Impostazioni</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <TabsContent value="pending" className="space-y-4">
                {filterBarJSX}
                {pendingBookings.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">
                        {hasActiveFilters ? 'Nessun risultato con i filtri applicati' : 'Nessuna prenotazione in attesa'}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  pendingBookings.map(booking => renderBookingCard(booking))
                )}
              </TabsContent>

              <TabsContent value="confirmed" className="space-y-4">
                {filterBarJSX}
                {confirmedBookings.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">
                        {hasActiveFilters ? 'Nessun risultato con i filtri applicati' : 'Nessuna prenotazione confermata'}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  confirmedBookings.map(booking => renderBookingCard(booking))
                )}
              </TabsContent>

              <TabsContent value="completed" className="space-y-4">
                {filterBarJSX}
                {completedBookings.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">
                        {hasActiveFilters ? 'Nessun risultato con i filtri applicati' : 'Nessun tagliando completato'}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  completedBookings.map(booking => renderBookingCard(booking, false))
                )}
              </TabsContent>

              <TabsContent value="cancelled" className="space-y-4">
                {filterBarJSX}
                {cancelledBookings.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">
                        {hasActiveFilters ? 'Nessun risultato con i filtri applicati' : 'Nessuna prenotazione annullata'}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  cancelledBookings.map(booking => renderBookingCard(booking, false))
                )}
              </TabsContent>

              {/* Calendar Tab */}
              <TabsContent value="calendar">
                <OutlookCalendarView token={token} onBookingUpdate={fetchBookings} refreshTrigger={calendarRefreshTrigger} />
              </TabsContent>

              {/* Customers Tab */}
              <TabsContent value="customers">
                <Card>
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <CardTitle className="font-heading flex items-center gap-2">
                          <Users className="w-5 h-5" />
                          Clienti Registrati ({customers.length})
                        </CardTitle>
                        <CardDescription>
                          Gestisci i tuoi clienti e invia comunicazioni - Ordinati alfabeticamente
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline"
                          onClick={selectAllCustomers}
                          disabled={filteredCustomers.length === 0}
                        >
                          <UserCheck className="w-4 h-4 mr-2" />
                          {selectedCustomers.length === filteredCustomers.length && filteredCustomers.length > 0 ? 'Deseleziona Tutti' : 'Seleziona Tutti'}
                        </Button>
                        <Button 
                          onClick={() => openEmailDialog()}
                          disabled={selectedCustomers.length === 0}
                          data-testid="send-email-selected-btn"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Invia Email ({selectedCustomers.length})
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Customer Search Filter */}
                    <div className="mb-6 p-4 bg-muted/30 rounded-lg border border-border/50" data-testid="customer-filter-bar">
                      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                        <div className="flex-1 relative w-full">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            placeholder="Cerca cliente per nome, email, telefono o modello..."
                            className="pl-10 h-10"
                            value={customerSearchText}
                            onChange={(e) => setCustomerSearchText(e.target.value)}
                            data-testid="customer-search-input"
                          />
                        </div>
                        {customerSearchText && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setCustomerSearchText('')}
                            data-testid="clear-customer-search-btn"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Cancella
                          </Button>
                        )}
                      </div>
                      {customerSearchText && (
                        <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                          <Filter className="w-4 h-4" />
                          <span>Trovati {filteredCustomers.length} clienti per "{customerSearchText}"</span>
                        </div>
                      )}
                    </div>
                    
                    {customers.length === 0 ? (
                      <div className="py-12 text-center">
                        <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">Nessun cliente registrato</p>
                      </div>
                    ) : filteredCustomers.length === 0 ? (
                      <div className="py-12 text-center">
                        <Search className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">Nessun cliente trovato per "{customerSearchText}"</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {filteredCustomers.map((customer) => (
                          <Card 
                            key={customer.id} 
                            className="border-border/50 hover:border-primary/30 hover:shadow-md transition-all cursor-pointer" 
                            data-testid={`customer-card-${customer.id}`}
                            onClick={() => openCustomerHistory(customer)}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-start gap-4">
                                <Checkbox
                                  checked={selectedCustomers.includes(customer.id)}
                                  onCheckedChange={() => toggleCustomerSelection(customer.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`customer-checkbox-${customer.id}`}
                                />
                                <div className="flex-1 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                  <div>
                                    <p className="text-xs text-muted-foreground">Nome</p>
                                    <p className="font-medium">{customer.cognome} {customer.nome}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-muted-foreground">Email</p>
                                    <p className="font-medium text-sm">{customer.email}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-muted-foreground">Telefono</p>
                                    <p className="font-medium">{customer.telefono}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-muted-foreground">Modello Moto</p>
                                    <p className="font-medium">{customer.modello_moto}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-muted-foreground">Tagliandi</p>
                                    <Badge variant="secondary" className="cursor-pointer">
                                      {customer.booking_count || 0} - Vedi storico →
                                    </Badge>
                                  </div>
                                  {customer.libretto_data && (
                                    <>
                                      <div>
                                        <p className="text-xs text-muted-foreground">Targa</p>
                                        <p className="font-medium">{customer.libretto_data.targa || 'N/D'}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-muted-foreground">Telaio</p>
                                        <p className="font-medium text-xs">{customer.libretto_data.telaio || 'N/D'}</p>
                                      </div>
                                    </>
                                  )}
                                </div>
                                <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
                                  {customer.has_libretto_image ? (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => viewCustomerLibretto(customer)}
                                      className="text-primary hover:text-primary hover:bg-primary/10"
                                      data-testid={`view-libretto-${customer.id}`}
                                      title="Visualizza Libretto"
                                    >
                                      <FileText className="w-4 h-4" />
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => openQuickLibrettoDialog(customer)}
                                      className="text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                                      data-testid={`upload-libretto-${customer.id}`}
                                      title="Carica Libretto"
                                    >
                                      <Upload className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openEmailDialog(customer.id)}
                                    data-testid={`email-customer-${customer.id}`}
                                    title="Invia Email"
                                  >
                                    <Mail className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openDeleteDialog(customer)}
                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                    data-testid={`delete-customer-${customer.id}`}
                                    title="Elimina Cliente"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Settings Tab */}
              <TabsContent value="settings">
                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Settings className="w-5 h-5" />
                      Personalizzazione
                    </CardTitle>
                    <CardDescription>
                      Configura le impostazioni del tuo negozio
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="mail_negozio">Email Negozio</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <Input
                          id="mail_negozio"
                          type="email"
                          placeholder="negozio@esempio.com"
                          className="pl-10 h-12"
                          value={settings.mail_negozio}
                          onChange={(e) => setSettings({ ...settings, mail_negozio: e.target.value })}
                          data-testid="settings-mail-negozio"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Email a cui riceverai le notifiche delle prenotazioni
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="numero_veicoli_giorno">Numero Veicoli al Giorno</Label>
                      <Input
                        id="numero_veicoli_giorno"
                        type="number"
                        min="1"
                        max="50"
                        className="h-12 w-32"
                        value={settings.numero_veicoli_giorno}
                        onChange={(e) => setSettings({ ...settings, numero_veicoli_giorno: parseInt(e.target.value) || 5 })}
                        data-testid="settings-numero-veicoli"
                      />
                      <p className="text-xs text-muted-foreground">
                        Numero massimo di tagliandi che il sistema permetterà di prenotare per ogni giorno
                      </p>
                    </div>

                    <div className="border-t pt-6">
                      <h3 className="font-heading font-bold mb-4">Configurazione SMTP (smtp2go)</h3>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="smtp_user">SMTP User</Label>
                          <Input
                            id="smtp_user"
                            type="email"
                            placeholder="utente@smtp2go.com"
                            className="h-12"
                            value={settings.smtp_user}
                            onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })}
                            data-testid="settings-smtp-user"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="smtp_pass">SMTP Password</Label>
                          <Input
                            id="smtp_pass"
                            type="password"
                            placeholder="••••••••"
                            className="h-12"
                            value={settings.smtp_pass}
                            onChange={(e) => setSettings({ ...settings, smtp_pass: e.target.value })}
                            data-testid="settings-smtp-pass"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Credenziali per l'invio delle email tramite SMTP2GO (host: mail-eu.smtp2go.com)
                      </p>
                    </div>

                    <div className="flex justify-end pt-4">
                      <Button 
                        onClick={handleSaveSettings}
                        disabled={isSavingSettings}
                        className="h-12 px-8"
                        data-testid="settings-save-btn"
                      >
                        {isSavingSettings ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Salvataggio...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Salva Impostazioni
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </>
          )}
        </Tabs>
      </main>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Dettagli Prenotazione</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Cliente</p>
                  <p className="font-medium">{selectedBooking.customer_nome} {selectedBooking.customer_cognome}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Modello</p>
                  <p className="font-medium">{selectedBooking.modello_moto}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="font-medium">{selectedBooking.customer_email}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Telefono</p>
                  <p className="font-medium">{selectedBooking.customer_telefono}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Data</p>
                  <p className="font-medium">
                    {format(new Date(selectedBooking.data_consegna), 'd MMMM yyyy', { locale: it })}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Orari</p>
                  <p className="font-medium">{selectedBooking.ora_consegna} - {selectedBooking.ora_ritiro}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Km</p>
                  <p className="font-medium">{selectedBooking.km_attuali}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Stato</p>
                  {getStatusBadge(selectedBooking.status)}
                </div>
              </div>
              
              {selectedBooking.note && (
                <div>
                  <p className="text-muted-foreground text-sm">Note cliente</p>
                  <p className="font-medium">{selectedBooking.note}</p>
                </div>
              )}

              {selectedBooking.lavori_effettuati && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-sm font-medium text-green-700 dark:text-green-300 mb-1">Lavori effettuati:</p>
                  <p className="text-sm text-green-600 dark:text-green-400 whitespace-pre-line">
                    {selectedBooking.lavori_effettuati}
                  </p>
                </div>
              )}

              {selectedBooking.foto_bolla && (
                <div>
                  <p className="text-muted-foreground text-sm mb-2">Foto bolla lavori</p>
                  <img 
                    src={`data:image/jpeg;base64,${selectedBooking.foto_bolla}`}
                    alt="Bolla lavori"
                    className="max-w-full rounded-lg border"
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={isActionOpen} onOpenChange={setIsActionOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {actionType === 'confirm' && 'Conferma Tagliando'}
              {actionType === 'complete' && 'Completa Tagliando'}
              {actionType === 'cancel' && 'Annulla Tagliando'}
            </DialogTitle>
          </DialogHeader>
          
          {actionType === 'confirm' && (
            <div className="space-y-4">
              <p>Confermi il tagliando per {selectedBooking?.customer_nome} {selectedBooking?.customer_cognome}?</p>
              <p className="text-sm text-muted-foreground">
                Una email di conferma sarà inviata al cliente con i dettagli dell'appuntamento.
              </p>
            </div>
          )}

          {actionType === 'complete' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Foto Bolla Lavori</Label>
                <div className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id="bolla-upload"
                    onChange={handleBollaChange}
                    data-testid="bolla-upload-input"
                  />
                  <label htmlFor="bolla-upload" className="cursor-pointer">
                    {bollaPreview ? (
                      <div className="space-y-2">
                        <img 
                          src={bollaPreview} 
                          alt="Anteprima bolla" 
                          className="max-h-40 mx-auto rounded"
                        />
                        <p className="text-sm text-muted-foreground">Clicca per cambiare</p>
                        <p className="text-xs text-primary">I lavori verranno estratti automaticamente con AI</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Upload className="w-8 h-8 mx-auto text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          Carica foto della bolla per estrazione automatica lavori
                        </p>
                        <p className="text-xs text-muted-foreground">
                          L'AI leggerà e compilerà l'elenco lavori
                        </p>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Lavori Effettuati</Label>
                <Textarea
                  placeholder="Descrivi i lavori effettuati..."
                  value={actionForm.lavori_effettuati}
                  onChange={(e) => setActionForm({ ...actionForm, lavori_effettuati: e.target.value })}
                  rows={4}
                  data-testid="lavori-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Note (opzionale)</Label>
                <Textarea
                  placeholder="Note aggiuntive per il cliente..."
                  value={actionForm.note_negozio}
                  onChange={(e) => setActionForm({ ...actionForm, note_negozio: e.target.value })}
                  rows={2}
                  data-testid="note-negozio-input"
                />
              </div>
            </div>
          )}

          {actionType === 'cancel' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Motivo dell'annullamento *</Label>
                <Textarea
                  placeholder="Inserisci il motivo dell'annullamento..."
                  value={actionForm.motivo_annullamento}
                  onChange={(e) => setActionForm({ ...actionForm, motivo_annullamento: e.target.value })}
                  rows={3}
                  required
                  data-testid="motivo-annullamento-input"
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Una email sarà inviata al cliente con il motivo dell'annullamento.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsActionOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleAction}
              disabled={isSubmitting || (actionType === 'cancel' && !actionForm.motivo_annullamento)}
              className={actionType === 'cancel' ? 'bg-red-600 hover:bg-red-700' : actionType === 'complete' ? 'bg-green-600 hover:bg-green-700' : ''}
              data-testid="action-confirm-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Elaborazione...
                </>
              ) : (
                <>
                  {actionType === 'confirm' && 'Conferma'}
                  {actionType === 'complete' && 'Completa'}
                  {actionType === 'cancel' && 'Annulla Tagliando'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Send className="w-5 h-5" />
              Invia Email
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">
                {selectedCustomers.length === 0 
                  ? `Invierai a tutti i ${customers.length} clienti`
                  : `Invierai a ${selectedCustomers.length} cliente/i selezionato/i`
                }
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email-subject">Oggetto *</Label>
              <Input
                id="email-subject"
                placeholder="Oggetto della email..."
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                data-testid="email-subject-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email-message">Messaggio *</Label>
              <Textarea
                id="email-message"
                placeholder="Scrivi il tuo messaggio..."
                value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                rows={6}
                data-testid="email-message-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEmailDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleSendEmail}
              disabled={isSendingEmail || !emailForm.subject || !emailForm.message}
              data-testid="send-email-confirm-btn"
            >
              {isSendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Invio in corso...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Invia Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Customer Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              Conferma Eliminazione Cliente
            </DialogTitle>
          </DialogHeader>
          
          {customerToDelete && (
            <div className="space-y-4">
              <div className="p-4 bg-destructive/10 rounded-lg border border-destructive/20">
                <p className="text-sm">
                  Stai per eliminare definitivamente il cliente:
                </p>
                <p className="font-bold text-lg mt-2">
                  {customerToDelete.cognome} {customerToDelete.nome}
                </p>
                <p className="text-sm text-muted-foreground">
                  {customerToDelete.email}
                </p>
              </div>
              
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-destructive">⚠️ Questa azione è irreversibile!</p>
                <p className="mt-2">Verranno eliminati:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Il profilo del cliente</li>
                  <li>Tutte le {customerToDelete.booking_count || 0} prenotazioni associate</li>
                  <li>Lo storico completo dei tagliandi</li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeletingCustomer}
            >
              Annulla
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteCustomer}
              disabled={isDeletingCustomer}
              data-testid="confirm-delete-customer-btn"
            >
              {isDeletingCustomer ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Eliminazione...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina Cliente
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer History Dialog */}
      <Dialog open={isCustomerHistoryOpen} onOpenChange={setIsCustomerHistoryOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Storico Tagliandi
            </DialogTitle>
          </DialogHeader>
          
          {selectedCustomerForHistory && (
            <div className="space-y-6">
              {/* Customer Info Header */}
              <Card className="bg-muted/30">
                <CardContent className="p-4">
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Cliente</p>
                      <p className="font-bold text-lg">{selectedCustomerForHistory.cognome} {selectedCustomerForHistory.nome}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="font-medium">{selectedCustomerForHistory.email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Telefono</p>
                      <p className="font-medium">{selectedCustomerForHistory.telefono}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Modello</p>
                      <p className="font-medium">{selectedCustomerForHistory.modello_moto}</p>
                    </div>
                    {selectedCustomerForHistory.libretto_data && (
                      <>
                        <div>
                          <p className="text-xs text-muted-foreground">Targa</p>
                          <p className="font-medium">{selectedCustomerForHistory.libretto_data.targa || 'N/D'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Telaio</p>
                          <p className="font-medium text-sm">{selectedCustomerForHistory.libretto_data.telaio || 'N/D'}</p>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Libretto Image or Upload Section */}
                  <div className="mt-4 pt-4 border-t border-border/50">
                    {selectedCustomerForHistory.libretto_image ? (
                      <>
                        <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                          <ImageIcon className="w-3 h-3" /> Foto Libretto
                        </p>
                        <div 
                          className="relative inline-block cursor-pointer group"
                          onClick={() => openImageViewer(selectedCustomerForHistory.libretto_image, 'Libretto di Circolazione')}
                        >
                          <img 
                            src={`data:image/jpeg;base64,${selectedCustomerForHistory.libretto_image}`}
                            alt="Libretto di circolazione"
                            className="max-h-32 rounded-lg border border-border shadow-sm group-hover:opacity-90 transition-opacity"
                          />
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="bg-black/50 rounded-full p-2">
                              <ZoomIn className="w-5 h-5 text-white" />
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-3">
                        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Libretto mancante</p>
                              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                                Il cliente non ha caricato il libretto. Puoi caricarlo tu.
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            id="shop-libretto-upload"
                            onChange={handleShopLibrettoFileChange}
                            data-testid="shop-libretto-upload-input"
                          />
                          <label htmlFor="shop-libretto-upload" className="cursor-pointer">
                            {shopLibrettoPreview ? (
                              <div className="space-y-2">
                                <img 
                                  src={shopLibrettoPreview} 
                                  alt="Anteprima libretto" 
                                  className="max-h-24 mx-auto rounded"
                                />
                                <p className="text-xs text-muted-foreground">Clicca per cambiare</p>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <Upload className="w-6 h-6 mx-auto text-muted-foreground" />
                                <p className="text-sm text-muted-foreground">
                                  Carica foto libretto
                                </p>
                              </div>
                            )}
                          </label>
                        </div>
                        
                        {shopLibrettoPreview && (
                          <Button 
                            onClick={uploadShopLibretto} 
                            disabled={isUploadingShopLibretto}
                            size="sm"
                            className="w-full"
                            data-testid="shop-upload-libretto-btn"
                          >
                            {isUploadingShopLibretto ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Elaborazione...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Carica Libretto
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Loading indicator */}
              {isLoadingHistory && (
                <div className="py-8 text-center">
                  <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary mb-2" />
                  <p className="text-muted-foreground">Caricamento storico...</p>
                </div>
              )}

              {/* Bookings History */}
              {!isLoadingHistory && (
              <div>
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Wrench className="w-5 h-5" />
                  Tagliandi ({customerBookings.length})
                </h3>
                
                {customerBookings.length === 0 ? (
                  <div className="py-8 text-center bg-muted/20 rounded-lg">
                    <CalendarIcon className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">Nessun tagliando registrato per questo cliente</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {customerBookings.map((booking, index) => (
                      <Card key={booking.id} className="border-border/50">
                        <CardContent className="p-4">
                          <div className="flex flex-col gap-4">
                            {/* Header with status and date */}
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex items-center gap-3">
                                <span className="text-sm text-muted-foreground">#{customerBookings.length - index}</span>
                                {getStatusBadge(booking.status)}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {new Date(booking.data_consegna).toLocaleDateString('it-IT', {
                                  weekday: 'long',
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric'
                                })}
                              </div>
                            </div>
                            
                            {/* Booking details */}
                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                              <div>
                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Clock className="w-3 h-3" /> Consegna
                                </p>
                                <p className="font-medium">{booking.ora_consegna}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Clock className="w-3 h-3" /> Ritiro
                                </p>
                                <p className="font-medium">{booking.ora_ritiro}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Bike className="w-3 h-3" /> KM
                                </p>
                                <p className="font-medium">{booking.km_attuali || 'N/D'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Bike className="w-3 h-3" /> Modello
                                </p>
                                <p className="font-medium text-sm">{booking.modello_moto}</p>
                              </div>
                            </div>

                            {/* Notes from customer */}
                            {booking.note_cliente && (
                              <div className="bg-muted/30 p-3 rounded-lg">
                                <p className="text-xs text-muted-foreground mb-1">Note cliente</p>
                                <p className="text-sm">{booking.note_cliente}</p>
                              </div>
                            )}

                            {/* Work done (for completed) */}
                            {booking.status === 'completed' && booking.lavori_effettuati && (
                              <div className="bg-green-50 dark:bg-green-950/30 p-3 rounded-lg border border-green-200 dark:border-green-900">
                                <p className="text-xs text-green-700 dark:text-green-400 mb-1 flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" /> Lavori effettuati
                                </p>
                                <p className="text-sm whitespace-pre-line">{booking.lavori_effettuati}</p>
                              </div>
                            )}

                            {/* Bolla Image (for completed bookings) */}
                            {booking.status === 'completed' && booking.foto_bolla && (
                              <div className="bg-green-50 dark:bg-green-950/30 p-3 rounded-lg border border-green-200 dark:border-green-900">
                                <p className="text-xs text-green-700 dark:text-green-400 mb-2 flex items-center gap-1">
                                  <ImageIcon className="w-3 h-3" /> Foto Bolla Lavori
                                </p>
                                <div 
                                  className="relative inline-block cursor-pointer group"
                                  onClick={() => openImageViewer(booking.foto_bolla, `Bolla Lavori - ${new Date(booking.data_consegna).toLocaleDateString('it-IT')}`)}
                                >
                                  <img 
                                    src={`data:image/jpeg;base64,${booking.foto_bolla}`}
                                    alt="Bolla lavori"
                                    className="max-h-40 rounded-lg border border-green-300 shadow-sm group-hover:opacity-90 transition-opacity"
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="bg-black/50 rounded-full p-2">
                                      <ZoomIn className="w-5 h-5 text-white" />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Shop notes */}
                            {booking.note_negozio && (
                              <div className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded-lg border border-blue-200 dark:border-blue-900">
                                <p className="text-xs text-blue-700 dark:text-blue-400 mb-1">Note negozio</p>
                                <p className="text-sm">{booking.note_negozio}</p>
                              </div>
                            )}

                            {/* Cancellation reason */}
                            {booking.status === 'cancelled' && booking.motivo_annullamento && (
                              <div className="bg-red-50 dark:bg-red-950/30 p-3 rounded-lg border border-red-200 dark:border-red-900">
                                <p className="text-xs text-red-700 dark:text-red-400 mb-1 flex items-center gap-1">
                                  <XCircle className="w-3 h-3" /> Motivo annullamento
                                </p>
                                <p className="text-sm">{booking.motivo_annullamento}</p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCustomerHistoryOpen(false)}>
              Chiudi
            </Button>
            <Button 
              onClick={() => {
                setIsCustomerHistoryOpen(false);
                openEmailDialog(selectedCustomerForHistory?.id);
              }}
              disabled={!selectedCustomerForHistory}
            >
              <Mail className="w-4 h-4 mr-2" />
              Invia Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog open={isImageViewerOpen} onOpenChange={setIsImageViewerOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[95vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              {viewerTitle}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex items-center justify-center p-4 bg-muted/20 rounded-lg">
            {viewerImage && (
              <img 
                src={`data:image/jpeg;base64,${viewerImage}`}
                alt={viewerTitle}
                className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
              />
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsImageViewerOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Libretto Upload Dialog */}
      <Dialog open={isQuickLibrettoDialogOpen} onOpenChange={setIsQuickLibrettoDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Carica Libretto
            </DialogTitle>
          </DialogHeader>
          
          {quickLibrettoCustomer && (
            <div className="space-y-4">
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">Cliente:</p>
                <p className="font-bold">{quickLibrettoCustomer.cognome} {quickLibrettoCustomer.nome}</p>
                <p className="text-sm text-muted-foreground">{quickLibrettoCustomer.email}</p>
              </div>
              
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  id="quick-libretto-upload"
                  onChange={handleQuickLibrettoFileChange}
                  data-testid="quick-libretto-upload-input"
                />
                <label htmlFor="quick-libretto-upload" className="cursor-pointer">
                  {quickLibrettoPreview ? (
                    <div className="space-y-3">
                      <img 
                        src={quickLibrettoPreview} 
                        alt="Anteprima libretto" 
                        className="max-h-40 mx-auto rounded-lg"
                      />
                      <p className="text-sm text-muted-foreground">Clicca per cambiare immagine</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Upload className="w-10 h-10 mx-auto text-muted-foreground" />
                      <div>
                        <p className="font-medium">Carica foto del libretto</p>
                        <p className="text-sm text-muted-foreground">
                          I dati verranno estratti automaticamente
                        </p>
                      </div>
                    </div>
                  )}
                </label>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setIsQuickLibrettoDialogOpen(false)}
              disabled={isUploadingQuickLibretto}
            >
              Annulla
            </Button>
            <Button 
              onClick={uploadQuickLibretto}
              disabled={!quickLibrettoFile || isUploadingQuickLibretto}
              data-testid="confirm-quick-libretto-btn"
            >
              {isUploadingQuickLibretto ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Elaborazione...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Carica Libretto
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create Booking Dialog */}
      <Dialog open={isCreateBookingOpen} onOpenChange={setIsCreateBookingOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              {createBookingStep === 'select' && 'Nuovo Tagliando - Seleziona Cliente'}
              {createBookingStep === 'select_vehicle' && 'Nuovo Tagliando - Seleziona Veicolo'}
              {createBookingStep === 'new_customer' && 'Nuovo Tagliando - Nuovo Cliente'}
              {createBookingStep === 'booking_details' && 'Nuovo Tagliando - Dettagli Prenotazione'}
            </DialogTitle>
          </DialogHeader>

          {/* Step 1: Select Customer or Create New */}
          {createBookingStep === 'select' && (
            <div className="space-y-4">
              <div className="flex flex-col gap-4">
                <Button 
                  variant="outline" 
                  onClick={goToNewCustomerForm}
                  className="w-full justify-start h-auto p-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Plus className="w-5 h-5 text-primary" />
                    </div>
                    <div className="text-left">
                      <div className="font-medium">Crea Nuovo Cliente</div>
                      <div className="text-sm text-muted-foreground">Registra un nuovo cliente e prenota il tagliando</div>
                    </div>
                  </div>
                </Button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">oppure seleziona cliente esistente</span>
                </div>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Cerca per nome, email, telefono o modello..."
                  value={customerSearchForBooking}
                  onChange={(e) => setCustomerSearchForBooking(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="max-h-[300px] overflow-y-auto space-y-2">
                {filteredCustomersForBooking.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">
                    {customerSearchForBooking ? 'Nessun cliente trovato' : 'Cerca un cliente o creane uno nuovo'}
                  </p>
                ) : (
                  filteredCustomersForBooking.map(customer => (
                    <div
                      key={customer.id}
                      onClick={() => selectExistingCustomer(customer)}
                      className="p-3 border rounded-lg hover:bg-muted cursor-pointer transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{customer.cognome} {customer.nome}</div>
                          <div className="text-sm text-muted-foreground">{customer.email}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm">{customer.modello_moto}</div>
                          <div className="text-xs text-muted-foreground">{customer.telefono}</div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Step 1.5: Select Vehicle (for existing customers with multiple vehicles) */}
          {createBookingStep === 'select_vehicle' && (
            <div className="space-y-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setSelectedCustomerForBooking(null);
                  setCustomerVehicles([]);
                  setCreateBookingStep('select');
                }}
                className="mb-2"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Cambia Cliente
              </Button>

              {/* Selected customer info */}
              <Card className="bg-muted/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-medium">
                        {selectedCustomerForBooking?.cognome} {selectedCustomerForBooking?.nome}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {selectedCustomerForBooking?.email}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div>
                <Label className="text-base font-medium">Seleziona il veicolo per il tagliando</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Questo cliente ha {customerVehicles.length} veicoli registrati
                </p>
              </div>

              {isLoadingVehicles ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : (
                <div className="space-y-2">
                  {customerVehicles.map(vehicle => (
                    <div
                      key={vehicle.id}
                      onClick={() => {
                        setSelectedVehicleForBooking(vehicle);
                        setCreateBookingStep('booking_details');
                      }}
                      className="p-4 border rounded-lg hover:bg-muted cursor-pointer transition-colors flex items-center gap-4"
                    >
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Bike className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">{vehicle.modello_moto}</div>
                        {vehicle.nickname && (
                          <div className="text-sm text-muted-foreground">"{vehicle.nickname}"</div>
                        )}
                        {vehicle.libretto_data?.targa && (
                          <div className="text-xs text-muted-foreground">Targa: {vehicle.libretto_data.targa}</div>
                        )}
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: New Customer Form */}
          {createBookingStep === 'new_customer' && (
            <div className="space-y-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setCreateBookingStep('select')}
                className="mb-2"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Indietro
              </Button>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input
                    value={newCustomerForm.nome}
                    onChange={(e) => setNewCustomerForm(prev => ({ ...prev, nome: e.target.value }))}
                    placeholder="Mario"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cognome *</Label>
                  <Input
                    value={newCustomerForm.cognome}
                    onChange={(e) => setNewCustomerForm(prev => ({ ...prev, cognome: e.target.value }))}
                    placeholder="Rossi"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newCustomerForm.email}
                  onChange={(e) => setNewCustomerForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="mario.rossi@email.com"
                />
              </div>

              <div className="space-y-2">
                <Label>Telefono *</Label>
                <Input
                  type="tel"
                  value={newCustomerForm.telefono}
                  onChange={(e) => setNewCustomerForm(prev => ({ ...prev, telefono: e.target.value }))}
                  placeholder="+39 333 1234567"
                />
              </div>

              {/* Series Filter */}
              <div className="space-y-2">
                <Label>Filtra per Serie</Label>
                <Select 
                  value={selectedSeriesForNewCustomer} 
                  onValueChange={setSelectedSeriesForNewCustomer}
                >
                  <SelectTrigger>
                    <Filter className="w-4 h-4 text-muted-foreground mr-2" />
                    <SelectValue placeholder="Tutte le serie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le serie</SelectItem>
                    {modelSeries.map((series) => (
                      <SelectItem key={series.id} value={series.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{series.name}</span>
                          <span className="text-xs text-muted-foreground">({series.models.length} modelli)</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Model Selection */}
              <div className="space-y-2">
                <Label>Modello Moto *</Label>
                <Select
                  value={newCustomerForm.modello_moto}
                  onValueChange={(value) => setNewCustomerForm(prev => ({ ...prev, modello_moto: value }))}
                >
                  <SelectTrigger>
                    <Bike className="w-4 h-4 text-muted-foreground mr-2" />
                    <SelectValue placeholder="Seleziona modello" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {selectedSeriesForNewCustomer && selectedSeriesForNewCustomer !== 'all' ? (
                      // Show models of selected series
                      filteredModelsForNewCustomer.map((model) => (
                        <SelectItem key={model.id} value={model.id}>
                          <div className="flex items-center gap-3">
                            <img 
                              src={model.image} 
                              alt={model.name}
                              className="w-8 h-8 object-cover rounded"
                            />
                            <span>{model.name}</span>
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      // Show all models grouped by series
                      modelSeries.map((series) => (
                        <div key={series.id}>
                          <div className="px-2 py-1.5 text-xs font-semibold text-primary uppercase tracking-wider bg-primary/5 sticky top-0">
                            {series.name}
                          </div>
                          {series.models.map((model) => (
                            <SelectItem key={model.id} value={model.id}>
                              <div className="flex items-center gap-3">
                                <img 
                                  src={model.image} 
                                  alt={model.name}
                                  className="w-8 h-8 object-cover rounded"
                                />
                                <span>{model.name}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </div>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Password Account *</Label>
                <Input
                  type="text"
                  value={newCustomerForm.password}
                  onChange={(e) => setNewCustomerForm(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Password che il cliente userà per accedere"
                />
                <p className="text-xs text-muted-foreground">Verrà inviata al cliente via email</p>
              </div>

              <div className="space-y-2">
                <Label>Foto Libretto (opzionale)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleNewCustomerLibrettoChange}
                    className="flex-1"
                  />
                </div>
                {newCustomerLibrettoPreview && (
                  <img 
                    src={newCustomerLibrettoPreview} 
                    alt="Preview libretto" 
                    className="mt-2 max-h-32 rounded border"
                  />
                )}
              </div>

              <Button 
                onClick={goToBookingDetails} 
                className="w-full"
                disabled={!newCustomerForm.nome || !newCustomerForm.cognome || !newCustomerForm.email || !newCustomerForm.telefono || !newCustomerForm.modello_moto || !newCustomerForm.password}
              >
                Continua
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 3: Booking Details */}
          {createBookingStep === 'booking_details' && (
            <div className="space-y-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  if (selectedCustomerForBooking) {
                    // Go back to vehicle selection if multiple vehicles, else back to customer selection
                    if (customerVehicles.length > 1) {
                      setCreateBookingStep('select_vehicle');
                    } else {
                      setSelectedCustomerForBooking(null);
                      setSelectedVehicleForBooking(null);
                      setCustomerVehicles([]);
                      setCreateBookingStep('select');
                    }
                  } else {
                    setCreateBookingStep('new_customer');
                  }
                }}
                className="mb-2"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Indietro
              </Button>

              {/* Selected customer and vehicle info */}
              <Card className="bg-muted/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">
                        {selectedCustomerForBooking 
                          ? `${selectedCustomerForBooking.cognome} ${selectedCustomerForBooking.nome}`
                          : `${newCustomerForm.cognome} ${newCustomerForm.nome}`}
                      </div>
                      <div className="text-sm text-muted-foreground flex items-center gap-2">
                        <Bike className="w-3 h-3" />
                        {selectedVehicleForBooking?.modello_moto || newCustomerForm.modello_moto}
                        {selectedVehicleForBooking?.nickname && (
                          <span className="text-xs">"{selectedVehicleForBooking.nickname}"</span>
                        )}
                      </div>
                    </div>
                    {!selectedCustomerForBooking && (
                      <Badge className="ml-auto">Nuovo Cliente</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-2">
                <Label>Data Tagliando *</Label>
                <Input
                  type="date"
                  value={bookingForm.data_consegna}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, data_consegna: e.target.value }))}
                  min={getMinDate()}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ora Consegna *</Label>
                  <Select
                    value={bookingForm.ora_consegna}
                    onValueChange={handleConsegnaChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona ora" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ora Ritiro *</Label>
                  <Select
                    value={bookingForm.ora_ritiro}
                    onValueChange={(value) => setBookingForm(prev => ({ ...prev, ora_ritiro: value }))}
                    disabled={!bookingForm.ora_consegna}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={bookingForm.ora_consegna ? "Seleziona ora" : "Prima seleziona consegna"} />
                    </SelectTrigger>
                    <SelectContent>
                      {getPickupSlots(bookingForm.ora_consegna).map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>KM Attuali *</Label>
                <Input
                  type="number"
                  value={bookingForm.km_attuali}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, km_attuali: e.target.value }))}
                  placeholder="Es: 12500"
                />
              </div>

              <div className="space-y-2">
                <Label>Note (opzionale)</Label>
                <Textarea
                  value={bookingForm.note}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, note: e.target.value }))}
                  placeholder="Note aggiuntive per il tagliando..."
                  rows={3}
                />
              </div>

              <div className="bg-green-50 dark:bg-green-950/30 p-3 rounded-lg text-sm text-green-700 dark:text-green-300">
                <CheckCircle2 className="w-4 h-4 inline mr-2" />
                Il tagliando verrà creato direttamente in stato <strong>CONFERMATO</strong> e il cliente riceverà un'email di conferma.
              </div>

              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setIsCreateBookingOpen(false)}
                  disabled={isCreatingBooking}
                >
                  Annulla
                </Button>
                <Button 
                  onClick={createBookingFromShop}
                  disabled={isCreatingBooking || !bookingForm.data_consegna || !bookingForm.ora_consegna || !bookingForm.km_attuali}
                >
                  {isCreatingBooking ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creazione...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Crea e Conferma Tagliando
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Loading Overlay for uploads */}
      <LoadingOverlay 
        isVisible={isUploadingShopLibretto || isUploadingQuickLibretto || (isSubmitting && bollaFile)}
        message={
          isUploadingShopLibretto || isUploadingQuickLibretto 
            ? "Elaborazione libretto..." 
            : "Elaborazione bolla lavori..."
        }
        submessage={
          isUploadingShopLibretto || isUploadingQuickLibretto
            ? "Stiamo estraendo i dati dal documento con l'AI"
            : "Stiamo estraendo l'elenco dei lavori con l'AI"
        }
      />
    </div>
  );
};

export default ShopDashboard;
